import React, { useState, useMemo } from "react";
import {
    Box,
    Typography,
    Divider,
    Stack,
    Popover,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    FormGroup,
    FormControlLabel,
    Checkbox,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";

const formatMethod = (method) => {
    if (!method) return "—";
    return method
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const LogsFilter = ({ logs, onApplyFilters, activeFilters }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [methodFilter, setMethodFilter] = useState(
        activeFilters.method || ""
    );
    const [statusFilter, setStatusFilter] = useState(
        activeFilters.status || {
            success: true,
            error: true,
        }
    );

    const uniqueMethods = useMemo(() => {
        const methods = logs
            .map((log) => log.method)
            .filter((method) => method)
            .filter((method, index, self) => self.indexOf(method) === index)
            .sort();

        return methods;
    }, [logs]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleStatusChange = (status) => {
        setStatusFilter((prev) => ({
            ...prev,
            [status]: !prev[status],
        }));
    };

    const handleClearFilters = () => {
        setMethodFilter("");
        setStatusFilter({ success: true, error: true });

        onApplyFilters({
            method: "",
            status: { success: true, error: true },
        });
    };

    const handleApplyFilters = () => {
        onApplyFilters({
            method: methodFilter,
            status: statusFilter,
        });
        handleClose();
    };

    const hasActiveFilters =
        methodFilter || !statusFilter.success || !statusFilter.error;

    const open = Boolean(anchorEl);
    const id = open ? "logs-filter-popover" : undefined;

    return (
        <>
            <Tooltip title="Filter logs">
                <IconButton
                    size="small"
                    onClick={handleClick}
                    color={hasActiveFilters ? "primary" : "default"}
                >
                    <FilterListIcon />
                </IconButton>
            </Tooltip>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    sx: { width: 300, p: 2 },
                }}
            >
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Filter Logs
                        </Typography>
                        <Divider />
                    </Box>

                    <FormControl fullWidth size="small">
                        <InputLabel>Method</InputLabel>
                        <Select
                            value={methodFilter}
                            label="Method"
                            onChange={(e) => setMethodFilter(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>All Methods</em>
                            </MenuItem>
                            {uniqueMethods.map((method) => (
                                <MenuItem key={method} value={method}>
                                    {formatMethod(method)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <Typography variant="body2" gutterBottom>
                            Status
                        </Typography>
                        <FormGroup row>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={statusFilter.success}
                                        onChange={() =>
                                            handleStatusChange("success")
                                        }
                                        size="small"
                                        color="success"
                                    />
                                }
                                label="Success"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={statusFilter.error}
                                        onChange={() =>
                                            handleStatusChange("error")
                                        }
                                        size="small"
                                        color="error"
                                    />
                                }
                                label="Error"
                            />
                        </FormGroup>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                    >
                        <Button
                            size="small"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                        >
                            Clear
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleApplyFilters}
                        >
                            Apply Filters
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
        </>
    );
};

export default LogsFilter;
